import { ISelectOption, Loading } from '@aon/aon-tws-common-ui';
import { QuestionList, QuestionListProps } from '@aon/tws-common-ui-questions/build/lib/components/questions/QuestionList';
import { ILibraryQuestion, ISortParam } from '@aon/tws-common-ui-questions/build/lib/models';
import * as React from 'react';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { IAddedQuestion, ISurveyQuestionEditModel } from '../../models';
import { LoadingWrapper } from '../common';
import { SelectScoredDimensionModal } from './SelectScoredDimensionModal';

export const NoResultsMessageWrapper = styled.div`
	text-align: center;
	padding-top: 50px;
	padding-bottom: 50px;
`;

const QuestionListContainer = styled.div<{ actions: boolean }>`
	${(props) =>
		props.actions === true
			? `.table td:last-child {
		text-align: right;
		min-width: 60px;
	}
	.table thead tr th:last-child {
		text-align: right;
		min-width: 60px;
	}
	.table thead tr th:last-child div {
		justify-content: flex-end;
	}`
			: ''}
	.tws-table-row-selected .tws-tooltip-container .tws-i-icon {
		color: #ffffff !important;
	}
`;
QuestionListContainer.displayName = 'QuestionListContainer';

const hiddenContainerStyle = {
	width: '0',
	opacity: 0,
	overflow: 'hidden',
	height: '0',
};

const filterOptionAll: ISelectOption = {
	name: 'All',
	displayText: 'All Dimensions',
	displayOrder: 1,
};
const filterOptionNone: ISelectOption = {
	name: 'None',
	displayText: 'No Dimensions',
	displayOrder: 2,
};

export interface ICommonQuestionListParams {
	addedQuestions: IAddedQuestion[];
	parentQuestion?: ISurveyQuestionEditModel;
	showSelectScoredDimensionModal: boolean;
	CancelSelectScoreDimensionModal: () => void;
	GetQuestions: (isDemographic: boolean) => void;
	GetBenchmarksIfNeeded: () => void;
	GetCulturesIfNeeded: () => void;
	SelectQuestion: (question: ILibraryQuestion, isSelected: boolean, parentQuestionId?: number) => void;
	SubmitScoredDimensionModal: (question: ILibraryQuestion | undefined, selectedDimension: string) => void;
}

export type IQuestionListProps = QuestionListProps & ICommonQuestionListParams;
interface IQuestionListFilterState {
	dimensionFilter: ISelectOption;
}

export class CommonQuestionList extends React.PureComponent<IQuestionListProps, IQuestionListFilterState> {
	constructor(props: IQuestionListProps) {
		super(props);
		this.state = {
			dimensionFilter: this.GetFilterOptions()[0],
		};

		this.ApplyPrimaryFilter = this.ApplyPrimaryFilter.bind(this);
		this.GetFilterOptions = this.GetFilterOptions.bind(this);
		this.GetFilteredQuestions = this.GetFilteredQuestions.bind(this);
		this.GetHeaderText = this.GetHeaderText.bind(this);
		this.HandleOnApply = this.HandleOnApply.bind(this);
		this.HandleSelectQuestion = this.HandleSelectQuestion.bind(this);
	}

	public componentDidMount() {
		this.props.GetQuestions(false);
		this.props.GetBenchmarksIfNeeded();
		this.props.GetCulturesIfNeeded();
	}

	public render() {
		const {
			columns,
			loading,
			parentQuestion,
			selectedBenchmarkIds,
			selectedCustomQuestionIds,
			selectedQuestionVariationIds,
			showSelectScoredDimensionModal,
			supportedCultures,
			CancelSelectScoreDimensionModal,
			SelectQuestionVariation,
			ShowAllVariations,
			ShowSingleVariation,
			SubmitScoredDimensionModal,
		} = this.props;
		const sortParams: ISortParam[] = [
			{ sortedBy: 'isCustomQuestion', sortAsc: 'asc' },
			{ sortedBy: 'dimensions', sortAsc: 'asc' },
		];
		const isFollowUp = parentQuestion !== undefined;
		const selectionThreshold = isFollowUp ? 1 : ContentConstants.SURVEYQUESTIONS_THRESHOLD;
		const selectionThresholdExceedText = isFollowUp ? undefined : ContentConstants.SURVEYQUESTIONS_THRESHOLD_EXCEEDED;

		return (
			<>
				{this.props.loading ? (
					<LoadingWrapper>
						<Loading height={'70px'} width={'70px'} />
					</LoadingWrapper>
				) : null}
				<QuestionListContainer id='question-list-container' actions={false} style={this.props.loading ? hiddenContainerStyle : undefined}>
					{this.GetHeaderText()}
					<QuestionList
						actionMenu={() => {
							/* */
						}}
						availableBenchmarks={this.props.availableBenchmarks}
						availableCultures={this.props.availableCultures}
						createQuestionButtons={() => {
							/* */
						}}
						columns={cloneDeep(columns)}
						disableWhenExceedingThreshold={isFollowUp}
						loading={loading}
						parentQuestionId={parentQuestion?.id}
						primaryFilterOptions={this.GetFilterOptions()}
						questions={this.GetFilteredQuestions()}
						selectable={true}
						selectedBenchmarkIds={selectedBenchmarkIds}
						selectedCustomQuestionIds={selectedCustomQuestionIds}
						selectedFilterOption={this.state.dimensionFilter}
						selectedQuestionVariationIds={selectedQuestionVariationIds}
						selectionThreshold={selectionThreshold}
						selectionThresholdExceedText={selectionThresholdExceedText}
						showFilterOption={true}
						sortParams={sortParams}
						supportedCultures={supportedCultures}
						totalSelectedQuestions={this.GetTotalSelectedQuestions()}
						variationActionMenu={() => {
							/* */
						}}
						ApplyPrimaryFilter={this.ApplyPrimaryFilter}
						LanguagesModalOpen={() => {
							/* */
						}}
						SelectQuestion={this.HandleSelectQuestion}
						SelectQuestionVariation={SelectQuestionVariation}
						ShowAllVariations={ShowAllVariations}
						ShowSingleVariation={ShowSingleVariation}
						UpdateColumns={this.HandleOnApply}
					/>
					<SelectScoredDimensionModal
						isOpen={showSelectScoredDimensionModal}
						onSubmit={SubmitScoredDimensionModal}
						onClose={() => CancelSelectScoreDimensionModal()}
					/>
				</QuestionListContainer>
			</>
		);
	}

	public ApplyPrimaryFilter = (selectedOption: ISelectOption) => {
		this.setState({ dimensionFilter: selectedOption });
	};

	// Primary filter options for Survey Builder are the dimension names associated with
	// the available questions.
	private GetFilterOptions = () => {
		let options: ISelectOption[] = [];
		if (this.props.parentQuestion !== undefined) {
			return options;
		}
		options = [{ ...filterOptionAll }];
		const dimensions: string[] = [];
		this.props.questions.forEach(
			(q: ILibraryQuestion) => q.dimensionNames && q.dimensionNames.forEach((d: string) => dimensions.indexOf(d) < 0 && dimensions.push(d)),
		);
		dimensions.sort();
		dimensions.forEach((d: string, index: number) => options.push({ name: d, displayText: d, displayOrder: index + 2 }));
		options.push({ ...filterOptionNone, displayOrder: options.length + 1 });
		return options;
	};

	private GetDimensionFilteredQuestions() {
		const { questions } = this.props;
		if (this.state.dimensionFilter === undefined || this.state.dimensionFilter.name === filterOptionAll.name) {
			return questions;
		} else if (this.state.dimensionFilter.name === filterOptionNone.name) {
			return questions.filter((q: ILibraryQuestion) => !q.dimensionNames || q.dimensionNames.length === 0);
		} else {
			return questions.filter((q: ILibraryQuestion) => q.dimensionNames && q.dimensionNames.indexOf(this.state.dimensionFilter.name) > -1);
		}
	}

	private GetFollowUpFilteredQuestions() {
		const questions = this.GetDimensionFilteredQuestions();
		return questions.filter((q: ILibraryQuestion) => q.tagNames && q.tagNames.includes('Follow-Up Question'));
	}

	private GetFilteredQuestions = () => {
		const { parentQuestion } = this.props;
		if (parentQuestion !== undefined) {
			return this.GetFollowUpFilteredQuestions();
		}
		return this.GetDimensionFilteredQuestions();
	};

	private GetHeaderText = () => {
		const { parentQuestion } = this.props;
		if (parentQuestion === undefined) {
			return <p>{ContentConstants.INSTRUCTIONALTEXT_CHOOSE_SURVEY_QUESTIONS}</p>;
		}
		return (
			<p>
				<b>{parentQuestion.text}</b>
				<br />
				{ContentConstants.INSTRUCTIONALTEXT_CHOOSE_FOLLOWUP_QUESTION}
			</p>
		);
	};

	private GetTotalSelectedQuestions = () => {
		const { addedQuestions, parentQuestion, totalSelectedQuestions } = this.props;
		// If there isn't a parentQuestion, totalSelectedQuestions is already calculated. Use the value from props.
		if (parentQuestion === undefined) {
			return totalSelectedQuestions;
		}
		// If there is a parentQuestion, the total selected questions should just be scoped to selected follow-ups for the given parentQuestion.
		const followUpAddedQuestions = addedQuestions.filter((aq) => aq.parentQuestionId == parentQuestion?.id);
		// When multiple follow-up questions can be assigned to the same parent question, combine this result with props.selectedQuestions.
		return followUpAddedQuestions.length;
	};

	private HandleOnApply(values?: any) {
		this.props.UpdateColumns(values);
	}

	private HandleSelectQuestion(question: ILibraryQuestion, isSelected: boolean) {
		const { parentQuestion } = this.props;
		this.props.SelectQuestion(question, isSelected, parentQuestion?.id);
	}
}
